import React, { useEffect, useState } from "react";
import Multiselect from "multiselect-react-dropdown";
import SlideGallery from "./SlideGallery";
import { notifySuccess, notifyError } from "../../Services/Notifier";
import Util from "../../Classes/Util";
import { SlideService } from "./Services/SlideService";
import { Variables } from "../../Variations/Variations";

const initialForm = {
    Title: "",
    ArTitle: "",
    Subtitle: "",
    ArSubtitle: "",
    PublishDate: "",
    IsPublished: false,
    Media: "",
};
const SlideModal = ({ show, onClose, slideId, onSaved }) => {
    const permissions = Util.decrypt(localStorage.getItem("Permissions"));
    const isNew = slideId === null;

    const [form, setForm] = useState(initialForm);
    const [sliders, setSliders] = useState([]);
    const [selectedSliders, setSelectedSliders] = useState([]);
    const [showingMedia, setShowingMedia] = useState("");
    const [saving, setSaving] = useState(false);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const loadData = async () => {
        try {
            // Load sliders first
            const allSliders = await SlideService.getSliders();
            setSliders(allSliders);

            if (!isNew) {
                // Load slide
                const slide = await SlideService.getById(slideId);
                setForm({
                    Title: slide.Title,
                    ArTitle: slide.ArTitle ?? "",
                    Subtitle: slide.Subtitle,
                    ArSubtitle: slide.ArSubtitle ?? "",
                    PublishDate: slide.PublishDate ?? "",
                    IsPublished: slide.IsPublished,
                    Media: slide.Media,
                });
                setShowingMedia(slide.Image);

                // 3️⃣ Load assigned slider IDs
                const assigned = await SlideService.getAssignedSliders(slideId);

                // 4️⃣ IMPORTANT: match by SliderId
                const matched = allSliders.filter(s =>
                    assigned.some(a => a.SliderId === s.SliderId)
                );

                setSelectedSliders(matched);
            } else {
                setForm(initialForm);
                setSelectedSliders([]);
                setShowingMedia("");
            }
        } catch (ex) {
            console.error(ex);
            notifyError("Failed to load slide data");
        }
    };


    const save = async () => {
        if (!form.Title || !form.Media) {
            notifyError("Title & Media required");
            return;
        }

        try {
            setSaving(true);

            const id = isNew
                ? await SlideService.create(form)
                : (await SlideService.update(slideId, form), slideId);

            await SlideService.assignSliders(id, selectedSliders);

            notifySuccess(isNew ? "Slide added" : "Slide updated");
            onSaved();
            onClose();
        } catch (ex) {
            notifyError(typeof ex === "string" ? ex : ex?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (show) loadData();
    }, [show]);

    if (!show) return null;

    return (
        <div className="modal fade show d-block" style={{ background: "#0006" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5>{isNew ? "Add Slide" : "Edit Slide"}</h5>
                        <button className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body">
                        {/* ---------- TITLE ---------- */}
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Title (EN)</label>
                                <input
                                    className="form-control"
                                    placeholder="Title"
                                    value={form.Title}
                                    onChange={(e) => update("Title", e.target.value)}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Title (AR)</label>
                                <input
                                    className="form-control text-end"
                                    dir="rtl"
                                    placeholder="Title"
                                    value={form.ArTitle}
                                    onChange={(e) => update("ArTitle", e.target.value)}
                                />
                            </div>
                        </div>

                        {/* ---------- SUBTITLE ---------- */}
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Subtitle (EN)</label>
                                <input
                                    className="form-control"
                                    placeholder="Subtitle"
                                    value={form.Subtitle}
                                    onChange={(e) => update("Subtitle", e.target.value)}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Subtitle (AR)</label>
                                <input
                                    className="form-control text-end"
                                    dir="rtl"
                                    placeholder="Subtitle"
                                    value={form.ArSubtitle}
                                    onChange={(e) => update("ArSubtitle", e.target.value)}
                                />
                            </div>
                        </div>


                        <SlideGallery
                            setMedia={(m) => update("Media", m)}
                            setShowingMedia={setShowingMedia}
                        />

                        {showingMedia && (
                            <img
                                src={Variables.API_PUBLISHED + showingMedia}
                                className="img-thumbnail my-3"
                                alt=""
                            />
                        )}

                        {permissions.PublishSlide && (
                            <input
                                type="datetime-local"
                                className="form-control mb-3"
                                value={form.PublishDate}
                                onChange={(e) => {
                                    update("PublishDate", e.target.value);
                                    update("IsPublished", true);
                                }}
                            />
                        )}

                        <Multiselect
                            options={sliders}
                            selectedValues={selectedSliders}
                            displayValue="SliderName"
                            onSelect={setSelectedSliders}
                            onRemove={setSelectedSliders}
                            placeholder="Select sliders"
                        />
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            disabled={saving}
                            onClick={save}
                        >
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SlideModal;
